import React, { useEffect, useId, useRef } from "react";
import { CheckCircle2, AlertCircle, Info } from "lucide-react";

export interface AlertModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  buttonLabel?: string;
  variant?: "success" | "error" | "info";
  onClose: () => void;
}

const variantIcon: Record<NonNullable<AlertModalProps["variant"]>, React.ReactNode> = {
  success: (
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
      <CheckCircle2 className="h-6 w-6" aria-hidden />
    </div>
  ),
  error: (
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15 text-red-400">
      <AlertCircle className="h-6 w-6" aria-hidden />
    </div>
  ),
  info: (
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-sky-500/15 text-sky-400">
      <Info className="h-6 w-6" aria-hidden />
    </div>
  ),
};

const variantButton: Record<NonNullable<AlertModalProps["variant"]>, string> = {
  success: "bg-emerald-600 hover:bg-emerald-700 text-white",
  error: "bg-red-600 hover:bg-red-700 text-white",
  info: "bg-sky-600 hover:bg-sky-700 text-white",
};

export const AlertModal: React.FC<AlertModalProps> = ({
  isOpen,
  title,
  message,
  buttonLabel = "OK",
  variant = "info",
  onClose,
}) => {
  const titleId = useId();
  const okRef = useRef<HTMLButtonElement>(null);
  const v = variant ?? "info";

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    okRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm iw-modal-backdrop-in"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="iw-modal-panel-in w-full max-w-md rounded-xl border border-slate-700 bg-slate-800 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4">{variantIcon[v]}</div>
        <h2 id={titleId} className="text-center text-lg font-semibold text-white">
          {title}
        </h2>
        <p className="mt-2 text-center text-sm text-slate-300">{message}</p>
        <div className="mt-6 flex justify-center sm:justify-end">
          <button
            ref={okRef}
            type="button"
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${variantButton[v]}`}
            onClick={onClose}
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
